<template>
  <v-container fluid>
    <v-alert v-if="error" type="error" class="mx-4 my-4">
      {{ error }}
    </v-alert>

    <v-row>
      <v-col cols="12" lg="5">
        <v-card class="my-6" elevation="3">
          <v-card-title class="d-flex align-center">
            <div>
              <v-icon class="mr-2" color="primary">mdi-map-marker-multiple</v-icon>
              Saved Weather Points
            </div>
            <v-spacer />
            <v-btn color="primary" size="small" @click="openAddPointDialog">
              <v-icon start>mdi-plus</v-icon>
              Add Point
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div class="d-flex flex-wrap ga-2 mb-4">
              <v-btn color="secondary" variant="tonal" @click="forceCapture" :loading="captureLoading">
                <v-icon start>mdi-cloud-upload-outline</v-icon>
                Capture Today's Weather Now
              </v-btn>
              <v-btn variant="text" @click="loadMonitorPoints" :loading="pointsLoading">
                Reload Points
              </v-btn>
            </div>

            <v-alert v-if="captureMessage" type="success" variant="tonal" class="mb-4">
              {{ captureMessage }}
            </v-alert>

            <v-data-table
              :headers="pointHeaders"
              :items="monitorPoints"
              :loading="pointsLoading"
              item-key="weatherMonitorPointId"
              density="comfortable"
              no-data-text="No weather points saved yet."
              hide-default-footer
              @click:row="onPointRowClick"
              :row-props="pointRowProps"
            >
              <template #item.zoneId="{ item }">
                {{ getZoneName(item.zoneId) }}
              </template>
              <template #item.isActive="{ item }">
                <v-chip size="small" :color="item.isActive ? 'success' : 'grey'" variant="tonal">
                  {{ item.isActive ? "Active" : "Off" }}
                </v-chip>
              </template>
              <template #item.coordinates="{ item }">
                {{ Number(item.latitude).toFixed(4) }}, {{ Number(item.longitude).toFixed(4) }}
              </template>
              <template #item.actions="{ item }">
                <v-btn size="small" variant="text" color="error" @click.stop="removePoint(item)">
                  Delete
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="7">
        <v-card class="my-6" elevation="3">
          <v-card-title class="d-flex align-center">
            <div>
              <v-icon class="mr-2" color="primary">mdi-weather-pouring</v-icon>
              Stored Daily Weather
              <span v-if="selectedPoint" class="text-subtitle-2 text-grey-darken-1 ml-2">
                for {{ selectedPoint.name }}
              </span>
            </div>
            <v-spacer />
            <v-btn v-if="selectedPoint" variant="text" size="small" @click="clearPointSelection">
              Clear Filter
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-row class="mb-2">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="historyFilters.fromDate"
                  type="date"
                  label="From Date"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="historyFilters.toDate"
                  type="date"
                  label="To Date"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn color="primary" variant="tonal" @click="loadHistory" :loading="historyLoading">
                  Reload History
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="historyHeaders"
              :items="displayHistoryRows"
              :loading="historyLoading"
              item-key="weatherDailyId"
              density="comfortable"
              no-data-text="No stored weather yet for the selected date range."
            >
              <template #item.weatherDate="{ item }">
                {{ formatShortDate(item.weatherDate) }}
              </template>
              <template #item.zoneId="{ item }">
                {{ getZoneName(item.zoneId) }}
              </template>
              <template #item.temperature="{ item }">
                <span v-if="item.temperature != null">{{ Number(item.temperature).toFixed(1) }}°C</span>
                <span v-else>-</span>
              </template>
              <template #item.windSpeed="{ item }">
                <span v-if="item.windSpeed != null">{{ Number(item.windSpeed).toFixed(1) }} m/s</span>
                <span v-else>-</span>
              </template>
              <template #item.precipitation="{ item }">
                <span v-if="item.precipitation != null">{{ Number(item.precipitation).toFixed(1) }} mm</span>
                <span v-else>-</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="pointDialogOpen" max-width="560px">
      <v-card>
        <v-card-title>Add Weather Point</v-card-title>
        <v-card-text>
          <v-text-field v-model="pointForm.name" label="Name" variant="outlined" density="comfortable" />
          <v-select
            v-model="pointForm.zoneId"
            :items="zoneOptions"
            item-title="label"
            item-value="value"
            label="Zone"
            variant="outlined"
            density="comfortable"
            clearable
          />
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model.number="pointForm.latitude"
                label="Latitude"
                type="number"
                step="0.000001"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="pointForm.longitude"
                label="Longitude"
                type="number"
                step="0.000001"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
          </v-row>
          <div class="text-caption text-grey-darken-1 mb-2">Click the map to place the point</div>
          <div ref="pointMapEl" class="point-picker-map mb-4"></div>
          <v-switch v-model="pointForm.isActive" color="primary" label="Active" hide-details />
          <div v-if="pointDialogError" class="text-error text-body-2 mt-3">{{ pointDialogError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closePointDialog">Cancel</v-btn>
          <v-btn color="primary" :loading="pointSaving" @click="savePoint">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  captureWeatherNow,
  deleteWeatherMonitorPoint,
  getWeatherHistory,
  getWeatherMonitorPoints,
  saveWeatherMonitorPoint,
} from "@/api/weather";
import { fetchZoneGeoJson, fetchZones } from "@/api/zones";
import { useAuthStore } from "@/stores/auth";

const auth = useAuthStore();
const error = ref(null);
const captureLoading = ref(false);
const captureMessage = ref("");
const pointsLoading = ref(false);
const historyLoading = ref(false);
const pointSaving = ref(false);
const pointDialogOpen = ref(false);
const pointDialogError = ref("");
const monitorPoints = ref([]);
const historyRows = ref([]);
const selectedPoint = ref(null);
const zones = ref([]);
const historyFilters = ref({
  fromDate: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  toDate: new Date().toISOString().slice(0, 10),
});
const pointForm = ref(createEmptyPointForm());
const pointMapEl = ref(null);
const defaultMapCenter = ref([53.1424, -7.6921]);
const defaultMapZoom = ref(7);
let pointMap = null;
let pointMarker = null;
let zoneCenterRequestToken = 0;

const pointHeaders = [
  { title: "Name", key: "name" },
  { title: "Zone", key: "zoneId" },
  { title: "Coordinates", key: "coordinates" },
  { title: "Status", key: "isActive", width: 100 },
  { title: "Actions", key: "actions", sortable: false, width: 110 },
];

const historyHeaders = [
  { title: "Date", key: "weatherDate", width: 120 },
  { title: "Point", key: "monitorPointName" },
  { title: "Zone", key: "zoneId" },
  { title: "Temp", key: "temperature", width: 90 },
  { title: "Wind", key: "windSpeed", width: 100 },
  { title: "Rain", key: "precipitation", width: 100 },
  { title: "Condition", key: "description" },
  { title: "Source", key: "source", width: 120 },
];

const displayHistoryRows = computed(() => {
  if (!selectedPoint.value) return historyRows.value;
  return historyRows.value.filter(
    (row) => Number(row.weatherMonitorPointId) === Number(selectedPoint.value.weatherMonitorPointId)
  );
});

const zoneOptions = computed(() =>
  (zones.value || []).map((zone) => ({
    label: zone.name || zone.Name || zone.zoneId || zone.ZoneId,
    value: zone.zoneId || zone.ZoneId,
  }))
);

const zoneNameById = computed(() => {
  const entries = (zones.value || []).map((zone) => [
    String(zone.zoneId || zone.ZoneId),
    zone.name || zone.Name || zone.zoneName || zone.ZoneName || zone.zoneId || zone.ZoneId,
  ]);
  return new Map(entries);
});

onMounted(async () => {
  await auth.init();
  await loadZones();
  await loadDefaultMapCenter();
  await Promise.all([loadMonitorPoints(), loadHistory()]);
});

onBeforeUnmount(() => {
  if (pointMap) {
    pointMap.remove();
    pointMap = null;
    pointMarker = null;
  }
});

function createEmptyPointForm() {
  return {
    name: "",
    zoneId: "",
    latitude: null,
    longitude: null,
    isActive: true,
  };
}

async function loadMonitorPoints() {
  pointsLoading.value = true;
  error.value = null;
  try {
    const response = await getWeatherMonitorPoints();
    monitorPoints.value = response.data || [];

    if (selectedPoint.value) {
      const freshSelected = monitorPoints.value.find(
        (row) => Number(row.weatherMonitorPointId) === Number(selectedPoint.value.weatherMonitorPointId)
      );
      selectedPoint.value = freshSelected || null;
    }
  } catch (e) {
    error.value = e.response?.data?.message || e.message || "Error loading weather monitor points";
  } finally {
    pointsLoading.value = false;
  }
}

async function loadHistory() {
  historyLoading.value = true;
  error.value = null;
  try {
    const response = await getWeatherHistory({
      fromDate: historyFilters.value.fromDate || undefined,
      toDate: historyFilters.value.toDate || undefined,
    });
    historyRows.value = response.data || [];
  } catch (e) {
    error.value = e.response?.data?.message || e.message || "Error loading weather history";
  } finally {
    historyLoading.value = false;
  }
}

async function loadZones() {
  try {
    zones.value = (await fetchZones()) || [];
  } catch {
    zones.value = [];
  }
}

async function loadDefaultMapCenter() {
  try {
    const firstZoneId = zones.value?.[0]?.zoneId || zones.value?.[0]?.ZoneId;
    if (!firstZoneId) return;

    const geojson = await fetchZoneGeoJson(firstZoneId);
    if (!geojson) return;

    const bounds = L.geoJSON(geojson).getBounds();
    if (!bounds.isValid()) return;

    const center = bounds.getCenter();
    defaultMapCenter.value = [Number(center.lat.toFixed(6)), Number(center.lng.toFixed(6))];
    defaultMapZoom.value = 11;
  } catch {
    // Fall back to Ireland if the first zone cannot be resolved.
  }
}

async function centerMapForZone(zoneId) {
  if (!zoneId) {
    if (pointMap) {
      pointMap.setView(defaultMapCenter.value, defaultMapZoom.value);
    }
    return;
  }

  const requestToken = ++zoneCenterRequestToken;

  try {
    const geojson = await fetchZoneGeoJson(zoneId);
    if (requestToken !== zoneCenterRequestToken || !geojson) return;

    const bounds = L.geoJSON(geojson).getBounds();
    if (!bounds.isValid()) return;

    const center = bounds.getCenter();
    const nextCenter = [Number(center.lat.toFixed(6)), Number(center.lng.toFixed(6))];
    defaultMapCenter.value = nextCenter;
    defaultMapZoom.value = 11;

    if (pointMap && (pointForm.value.latitude == null || pointForm.value.longitude == null)) {
      pointMap.setView(nextCenter, defaultMapZoom.value);
    }
  } catch {
    if (requestToken !== zoneCenterRequestToken) return;
    if (pointMap && (pointForm.value.latitude == null || pointForm.value.longitude == null)) {
      pointMap.setView(defaultMapCenter.value, defaultMapZoom.value);
    }
  }
}

async function forceCapture() {
  captureLoading.value = true;
  captureMessage.value = "";
  error.value = null;
  try {
    const response = await captureWeatherNow();
    const captured = response.data?.captured ?? 0;
    captureMessage.value = captured === 1 ? "Captured 1 weather row." : `Captured ${captured} weather rows.`;
    await Promise.all([loadMonitorPoints(), loadHistory()]);
  } catch (e) {
    error.value = e.response?.data?.message || e.message || "Failed to capture daily weather";
  } finally {
    captureLoading.value = false;
  }
}

function openAddPointDialog() {
  pointForm.value = createEmptyPointForm();
  pointForm.value.zoneId = zoneOptions.value?.[0]?.value ?? "";
  pointDialogError.value = "";
  pointDialogOpen.value = true;
}

function closePointDialog() {
  pointDialogOpen.value = false;
  pointDialogError.value = "";
}

watch(pointDialogOpen, async (isOpen) => {
  if (!isOpen) return;
  await nextTick();
  initPointMap();
  await centerMapForZone(pointForm.value.zoneId);
});

watch(
  () => [pointForm.value.latitude, pointForm.value.longitude],
  () => {
    syncPointMarker();
  }
);

watch(
  () => pointForm.value.zoneId,
  async (zoneId) => {
    if (!pointDialogOpen.value) return;
    if (pointForm.value.latitude != null && pointForm.value.longitude != null) return;
    await centerMapForZone(zoneId);
  }
);

async function savePoint() {
  pointDialogError.value = "";
  if (!pointForm.value.name?.trim()) {
    pointDialogError.value = "Name is required.";
    return;
  }
  if (pointForm.value.latitude == null || pointForm.value.longitude == null) {
    pointDialogError.value = "Latitude and longitude are required.";
    return;
  }

  pointSaving.value = true;
  try {
    await saveWeatherMonitorPoint({
      name: pointForm.value.name.trim(),
      zoneId: pointForm.value.zoneId || null,
      latitude: Number(pointForm.value.latitude),
      longitude: Number(pointForm.value.longitude),
      isActive: pointForm.value.isActive,
    });
    closePointDialog();
    await loadMonitorPoints();
  } catch (e) {
    pointDialogError.value = e.response?.data?.message || e.message || "Failed to save point";
  } finally {
    pointSaving.value = false;
  }
}

function initPointMap() {
  if (!pointMapEl.value) return;

  if (!pointMap) {
    pointMap = L.map(pointMapEl.value, {
      zoomControl: true,
    }).setView(defaultMapCenter.value, defaultMapZoom.value);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(pointMap);

    pointMap.on("click", (e) => {
      pointForm.value.latitude = Number(e.latlng.lat.toFixed(6));
      pointForm.value.longitude = Number(e.latlng.lng.toFixed(6));
      syncPointMarker();
    });
  }

  pointMap.invalidateSize();
  pointMap.setView(defaultMapCenter.value, defaultMapZoom.value);
  syncPointMarker();
}

function syncPointMarker() {
  if (!pointMap) return;

  const lat = Number(pointForm.value.latitude);
  const lng = Number(pointForm.value.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

  const latLng = [lat, lng];
  if (!pointMarker) {
    pointMarker = L.marker(latLng).addTo(pointMap);
  } else {
    pointMarker.setLatLng(latLng);
  }

  pointMap.setView(latLng, Math.max(pointMap.getZoom(), 11));
}

function onPointRowClick(_event, row) {
  selectedPoint.value = row?.item ?? row ?? null;
}

function clearPointSelection() {
  selectedPoint.value = null;
}

function pointRowProps({ item }) {
  const current = item?.weatherMonitorPointId ?? item?.raw?.weatherMonitorPointId;
  const selected = selectedPoint.value?.weatherMonitorPointId;
  return {
    class: Number(current) === Number(selected) ? "weather-point-row-selected" : "weather-point-row",
    style: "cursor:pointer;",
  };
}

function getZoneName(zoneId) {
  if (zoneId == null || zoneId === "") return "-";
  return zoneNameById.value.get(String(zoneId)) || String(zoneId);
}

async function removePoint(item) {
  if (!item?.weatherMonitorPointId) return;
  try {
    await deleteWeatherMonitorPoint(item.weatherMonitorPointId);
    if (selectedPoint.value?.weatherMonitorPointId === item.weatherMonitorPointId) {
      selectedPoint.value = null;
    }
    await Promise.all([loadMonitorPoints(), loadHistory()]);
  } catch (e) {
    error.value = e.response?.data?.message || e.message || "Failed to delete weather point";
  }
}

function formatShortDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
}
</script>

<style scoped>
:deep(.weather-point-row-selected) {
  background: rgba(25, 118, 210, 0.08);
}

.point-picker-map {
  height: 220px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}
</style>
